// Butler University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Butler's Common Data Set 2025-2026, which Institutional Research posts
//    one PDF per section on cdn.butler.edu (CDS_2025-2025_C.pdf and
//    CDS_2025-2025_G.pdf; the file names say 2025-2025, the pages say
//    "Common Data Set 2025-2026"). The SOURCES url is the butler.edu page
//    that links them. C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table; C8 test policy for Fall 2027; C21/C22 early plans
//    (the C21 counts are labeled "For the Fall 2025 entering class"); G1/G5
//    2026-2027 costs.
//  - Butler Admission: Applying as a First-Year Student (deadlines and
//    decision windows, Jordan College of the Arts requirements; the page's
//    class profile is the class entering fall 2026, so its deadlines are for
//    fall 2027 entry), Applying Early Decision (Nursing only), and
//    Test-Optional Admission.
//  - College Scorecard, school id 150163 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Butler Common Data Set 2025-2026',
        url: 'https://www.butler.edu/academics/institutional-research/about-institutional-research/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Butler Admission, Applying as a First-Year Student',
        url: 'https://www.butler.edu/admission-aid/first-year/applying-as-a-first-year-student/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Butler Admission, Applying Early Decision',
        url: 'https://www.butler.edu/admission-aid/first-year/early-decision/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Butler Admission, Test-Optional Admission',
        url: 'https://www.butler.edu/admission-aid/test-optional/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?150163-Butler-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'butler-university',
    kbId: 'butler_university',
    name: 'Butler University',
    shortName: 'Butler',
    location: 'Indianapolis, IN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Butler marketing color palette); decoration only.
    branding: {
        primary: '#13294B',
        secondary: '#00A3E0',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/butler_university_logo.png',
    },
    faq: {
        answer: { text: 'Butler University admitted 82.1% of first-year applicants for fall 2025, 9,144 of the 11,139 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Butler University acceptance rate?', a: 'For fall 2025 entry, Butler admitted 9,144 of 11,139 first-year applicants, an acceptance rate of 82.1%, and 1,292 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Butler acceptance rate for out-of-state students?', a: 'For fall 2025, Butler admitted 4,666 of 5,255 out-of-state first-year applicants, 88.8%. From Indiana, 4,291 of 5,481 applicants were admitted.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What are Butler\'s application deadlines for fall 2027?', a: 'Early Action is November 1, with decisions on a rolling basis from October to December. Regular Decision is February 1, with decisions from December to February, and Butler still accepts applications after that, with less chance at scholarships. Early Decision, also November 1, is only for Nursing applicants and is binding.', source: 'firstYear' },
            { q: 'Does Butler require the SAT or ACT?', a: 'No. Every applicant may apply with or without SAT or ACT scores, and if you apply test-optional, Butler does not use scores even if it has them. Butler suggests scores may help in its historically more selective programs, such as Biology, Chemistry and the College of Health Professions majors.', source: 'tests' },
            { q: 'What is the Butler application fee?', a: 'The application fee is $50, paid through the Common App.', source: 'firstYear' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11139,
        admitted: 9144,
        enrolled: 1292,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5481, admitted: 4291 },
            { group: 'Out-of-state', applied: 5255, admitted: 4666 },
            { group: 'International', applied: 403, admitted: 187 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Butler\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision (Nursing only)',
        note: 'Early decision is open only to Nursing applicants. The Common Data Set does not count early action applicants, so it gives no early action rate.',
        rows: [
            { plan: 'Early Decision', applied: 20, admitted: 17 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most majors need only the Common App. Two areas ask for more:',
        rows: [
            { school: 'Jordan College of the Arts', requirement: 'Art, Arts Administration, Dance, Music and Theatre majors complete an arts supplement with the Common App, then an audition, portfolio review and/or interview; Butler emails you to pick a date. Art History majors are exempt.', source: 'firstYear' },
            { school: 'Nursing', requirement: 'The only program with a binding Early Decision round (November 1). Early Decision applicants get priority admission consideration for Nursing.', source: 'earlyDecision' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Rolling, October to December', binding: false },
            { plan: 'Early Decision (Nursing only)', deadline: 'November 1', decision: 'December', binding: true },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Rolling, December to February', binding: false },
            { plan: 'Rolling admission', deadline: 'After February 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1. Butler strongly encourages applying by November 1 for the fullest scholarship and admission consideration; Early Action also brings priority consideration for the Honors Program. Applications are still accepted after February 1, with less chance at scholarships. The enrollment deposit is due May 1 (February 1 and $500, non-refundable, for Early Decision). The application fee is $50.',
        source: 'firstYear',
        binding: 'Early Decision is a binding commitment and only for Nursing applicants. If you apply this way, do not apply Early Decision or Single Choice/Restricted Early Action anywhere else; if admitted to Nursing, you withdraw all other applications and enroll at Butler.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Every applicant may apply with or without SAT or ACT scores. Butler suggests scores may help in historically more selective programs (Biochemistry, Biology, Chemistry, Exploratory Natural Sciences and the College of Health Professions majors), and homeschooled students and students whose schools give no grades are asked for scores unless they request test-optional review from their counselor. If you apply test-optional, scores are not used even if Butler has them. You may change your choice once before a decision, and the ACT science section is not required.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by July 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 50860 },
            { item: 'Required fees', amount: 990 },
            { item: 'Food and housing (on campus)', amount: 17730 },
            { item: 'Books and supplies', amount: 1100 },
            { item: 'Transportation', amount: 1400 },
            { item: 'Other expenses', amount: 3000 },
        ],
        total: 75080,
        note: 'For a first-year student living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 37499,
            byIncome: [
                { income: '$0 to $30,000', amount: 30817 },
                { income: '$30,001 to $48,000', amount: 29151 },
                { income: '$48,001 to $75,000', amount: 32800 },
                { income: '$75,001 to $110,000', amount: 38123 },
                { income: 'Over $110,000', amount: 43200 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Butler\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
